import { ref, computed, watch } from 'vue'
import store from '@/store'
import { useAdminDB } from '@/composables/useAdminDB'
import { filterOutBlacklisted } from '@/composables/useBlacklist'
import type { Conversation, DashboardData } from '@/types/dashboard'
import { showPushNotification } from '@/services/notifications'
import { withDefaultFluxos } from '@/composables/useHelpers'
import { pendingMonthFilter } from '@/composables/useChatTrigger'
import { pushAssignmentAlert } from '@/composables/useAssignmentAlert'
import { getPendingCallConv } from '@/services/callConvService'
import { onChatwootEvent, type ChatwootRealtimeEvent } from '@/composables/useChatwootRealtime'
import { translateChatwootEvent, translateMessageContent, type ConversationPatchFields } from '@/services/chatwootRealtimeTranslator'
import { currentOpenConvId } from '@/composables/useChatTrigger'
import { fetchConversationById } from '@/services/conversationService'
import { resolveRoleScope, useAdmin } from '@/composables/useAdmin'
import { useAgentChat } from '@/composables/useAgentChat'
import { headerLoading, headerLastUpdate, headerCountdown, headerMyFluxosFilter } from '@/composables/useHeaderState'
import { useConversationStore } from '@/composables/useConversationStore'
import { canAttempt, recordSuccess, recordFailure, secondsUntilRetry, circuitState } from '@/composables/useCircuitBreaker'
import { N8N_TEAMS_URL } from '@/api/n8n'
import { useTeamsWithMembers } from '@/composables/useTeamsWithMembers'
import type { TeamWithMembers } from '@/types/auth'
import { isFluxoHidden } from '@/composables/useHiddenFluxos'
import { logN8nBurstTiming } from '@/utils/n8nBurstTiming'
import { openInFocusMode } from '@/composables/useFocusModePreference'

// Duplicado de KpiGrid.vue (não de `import type ... from '*.vue'`): esse tipo
// importado de um SFC compila certinho no vue-tsc, mas quebra o build real
// (ts-loader, que processa .ts fora do pipeline do vue-loader, não enxerga
// exports nomeados de .vue — só o default do shim). União de 2 valores, não
// vale a dor de cabeça de extrair pra um arquivo de tipos só por isso.
export type VisaoGeralPeriodo = 'week' | 'month'

// ── Motor de dados do Dashboard ──────────────────────────────────────────
// Extraído de DashboardView.vue: antes, o polling (loadData + countdown do
// botão "Atualizar") e as notificações (nova conversa, atribuição, aviso de
// pendência do mês) só existiam enquanto o componente da view estava montado
// — sair da tela do Dashboard parava tudo (o countdown "congelava" e nada
// mais era buscado/notificado até voltar pra lá). Estado de módulo (mesmo
// padrão de useAutoLogout.ts/useConversationStore.ts) sobrevive à navegação
// entre rotas: só é destruído se a aba/app fechar. startDashboardEngine() é
// chamado uma vez em App.vue (raiz da SPA, nunca desmonta) assim que o login
// completa; DashboardView.vue chama de novo no próprio mount só pra esperar
// (idempotente — mesma promise) a primeira carga terminar antes de esconder
// o overlay.

const WEBHOOK             = N8N_TEAMS_URL
const AUTO_REFRESH        = 180
const CHATWOOT_ACCOUNT_ID = 3
const TOLERANCE_KEY       = 'dashboard_tolerance_mins'

const authUser    = computed(() => store.getters.authUser)
const userTeamIds = computed<number[]>(() => store.getters.userTeamIds)

const { conversations } = useConversationStore()

/* ── Estado exportado ─────────────────────────────────────────────────── */
export const allAgents          = ref<{ id: number; name: string; email: string; thumbnail: string; role: string; availability_status: string }[]>([])
export const allTeamsWithMembers = ref<TeamWithMembers[]>([])
export const dataStale          = ref(false)
export const changedIds         = ref<Set<number>>(new Set())
export const visaoGeralPeriodo  = ref<VisaoGeralPeriodo>('week')
export const toleranceMinutes   = ref<number>(parseInt(localStorage.getItem(TOLERANCE_KEY) || '10') || 10)
export const toastMessage       = ref('')
export const toastVisible       = ref(false)
export const toastError         = ref(false)

// Fase 2 do plano de carregamento progressivo (docs/planos/PLANO-SKELETON-DASHBOARD.md):
// fica true assim que conversations + userTeamIds (o gate real de escopo de
// permissão) estão prontos — não espera allAgents/allTeamsWithMembers/
// myFluxoIds, que continuam fire-and-forget dentro de startDashboardEngine()
// e não bloqueiam nada role-scoped. Complementa (não substitui) o overlay
// atual: quem quiser reagir granularmente (Fase 3+) lê isso direto, em vez
// de depender de DashboardView.vue já ter orquestrado o próprio overlay.
export const scopeReady = ref(false)

// Busca à parte pro badge "Mês" da Visão Geral (ver monthOnlyCount) — não
// reflete na tela, só serve pra comparar com o que já é exibido.
const monthScopeRaw = ref<Conversation[]>([])
const myFluxoIds     = ref<number[]>([])

interface ToleranceNotif {
  uid:         string
  convId:      number
  contactName: string
  waitLabel:   string
  team:        string | null
  agent:       string | null
  url:         string
}
export const activeNotifications = ref<ToleranceNotif[]>([])

const loading    = headerLoading
const lastUpdate = headerLastUpdate
const countdown  = headerCountdown

let changedClearTimer: ReturnType<typeof setTimeout> | null = null
let countdownId: ReturnType<typeof setInterval> | null = null
let currentFetchController: AbortController | null = null
let failCount = 0
let isFirstLoad = true
let toastTimer: ReturnType<typeof setTimeout> | null = null
const notifiedCycles = new Map<number, number>()
// Detecta troca de período (Tudo/Hoje/Semana/Mês) pra decidir se o ciclo
// precisa buscar tudo de novo — ver needsFullFetch em loadData().
let lastSinceISO: string | null | undefined = undefined
// myFluxoIds (perfil EC-ISBET, ver bloco fire-and-forget no fim de
// startDashboardEngine()) chega depois de scopeReady — sem essa flag, um
// ciclo leve rodando antes dela resolver trataria "ainda não sei" como "não
// atende fluxo nenhum" e restringiria a busca à toa.
let myFluxoIdsReady = false

/* ── Toast ────────────────────────────────────────────────────────────── */
export function toast(msg: string, err = false) {
  toastMessage.value = msg
  toastError.value   = err
  toastVisible.value = true
  if (toastTimer) clearTimeout(toastTimer)
  toastTimer = setTimeout(() => { toastVisible.value = false }, 3500)
}

/* ── Tolerância ───────────────────────────────────────────────────────── */
export function clampTolerance() {
  const v = toleranceMinutes.value
  if (!v || isNaN(v) || v < 1) { toleranceMinutes.value = 1; return }
  if (v > 240) toleranceMinutes.value = 240
}

function notifConvUrl(id: number): string {
  return `${window.location.origin}/?conv=${id}`
}

function fmtWaitMins(mins: number): string {
  if (mins < 60) return `${mins}min`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m > 0 ? `${h}h ${m}min` : `${h}h`
}

function addNotification(notif: ToleranceNotif) {
  if (activeNotifications.value.length >= 5) {
    activeNotifications.value.shift()
  }
  activeNotifications.value.push(notif)
  const uid = notif.uid
  setTimeout(() => dismissNotif(uid), 8000)
}

export function dismissNotif(uid: string) {
  const idx = activeNotifications.value.findIndex(n => n.uid === uid)
  if (idx !== -1) activeNotifications.value.splice(idx, 1)
}

function checkToleranceNotifications() {
  // Kill-switch global (Admin → Configurações Gerais → Notificações) — só
  // controla os cartões em tela (activeNotifications) e a notificação push
  // do SO abaixo; não mexe em nenhum outro dado (visibleConversations
  // continua sendo recalculada normalmente pra grade/KPIs).
  if (!useAdmin().globalSettings.value.toleranceNotificationsEnabled) return
  const scoped = visibleConversations.value
  if (!scoped.length) return
  const nowSecs = Date.now() / 1000
  const tolMins = Math.max(1, toleranceMinutes.value || 10)
  const currentIds = new Set(scoped.map(c => c.id))

  for (const id of notifiedCycles.keys()) {
    if (!currentIds.has(id)) notifiedCycles.delete(id)
  }

  // 1. Coleta todas as conversas que precisam de nova notificação neste ciclo
  const pending: Array<{ conv: Conversation; waitMins: number; url: string }> = []

  for (const conv of scoped) {
    if (!conv.waiting_since || conv.waiting_since === 0) continue
    if (conv.status !== 'open') continue

    const waitMins = Math.floor((nowSecs - conv.waiting_since) / 60)
    const cycle    = Math.floor(waitMins / tolMins)
    if (cycle < 1) continue

    const lastCycle = notifiedCycles.get(conv.id) ?? -1
    if (cycle <= lastCycle) continue

    notifiedCycles.set(conv.id, cycle)
    pending.push({ conv, waitMins, url: notifConvUrl(conv.id) })
  }

  if (pending.length === 0) return

  // 2. Notificações em tela — sempre individuais, limitadas a 5 pelo addNotification
  for (const { conv, waitMins, url } of pending) {
    addNotification({
      uid:         `${conv.id}-${Date.now()}`,
      convId:      conv.id,
      contactName: conv.contact.name,
      waitLabel:   fmtWaitMins(waitMins),
      team:        conv.team?.name ?? null,
      agent:       conv.assignee?.name ?? null,
      url,
    })
  }

  // 3. Push do sistema — individual se ≤ 2, agrupada se > 2
  if (pending.length <= 2) {
    for (const { conv, waitMins, url } of pending) {
      showPushNotification(
        `${conv.contact.name} aguarda resposta`,
        `${fmtWaitMins(waitMins)} esperando${conv.team ? ` · ${conv.team.name}` : ''}`,
        url,
        `conv-wait-${conv.id}`,
        conv.id,
      )
    }
  } else {
    // Destaca a mais antiga no corpo da notificação agrupada
    const longest = [...pending].sort((a, b) => b.waitMins - a.waitMins)[0]
    showPushNotification(
      `${pending.length} conversas aguardando resposta`,
      `Maior espera: ${longest.conv.contact.name} · ${fmtWaitMins(longest.waitMins)}`,
      notifConvUrl(longest.conv.id),
      'conv-wait-group',
      longest.conv.id,
    )
  }
}

watch(toleranceMinutes, () => {
  clampTolerance()
  localStorage.setItem(TOLERANCE_KEY, String(toleranceMinutes.value))
  notifiedCycles.clear()
  checkToleranceNotifications()
})

// "Geral" (fluxo_id 1) é forçado pra dentro de hiddenFluxoIds ao carregar a
// página (ALWAYS_HIDDEN_FLUXO_ID em useHiddenFluxos.ts) e também controla o
// filtro de request exclude_unassigned_no_team (ver loadData() abaixo) —
// conversa sem agente E sem time só existe enquanto ainda está com o robô/
// Typebot/IA, ou seja, ainda no fluxo Geral. Desmarcar "Geral" no seletor
// "Esconder Fluxo" do header muda esse filtro pro próximo fetch, mas sem
// recarregar agora o agente só veria essas conversas reaparecerem no
// próximo ciclo de polling — dispara loadData() na hora pra refletir já.
watch(() => isFluxoHidden(1), () => { loadData() })

/* ── Filtro por permissão (Regra Geral) ───────────────────────────────── */
function applyRoleScope(base: Conversation[]): Conversation[] {
  const uid   = authUser.value?.id
  const scope = resolveRoleScope(uid ?? 0, userTeamIds.value)

  // Administrador / EC - ISBET — travado pelo nome, ignora tudo abaixo
  if (scope.unrestricted) return base

  // 1ª prioridade — Somente atribuições do Agente
  if (scope.hasSomenteAgente) {
    return base.filter(c => c.assignee?.id === uid)
  }

  // 2ª prioridade — Atribuições do Agente e do(s) Time(s) reais do Chatwoot
  if (scope.hasAgenteETime) {
    return base.filter(c =>
      c.assignee?.id === uid || (scope.tids.length > 0 && c.team && scope.tids.includes(c.team.id))
    )
  }

  // 3ª prioridade — Todas as Atribuições
  if (scope.hasTodos) return base

  // Fallback (sem regra geral definida): mesmo comportamento de visaoSomenteAgente
  return base.filter(c => c.assignee?.id === uid)
}

export const roleFilteredConversations = computed(() => applyRoleScope(conversations.value))

// roleFilteredConversations + preferência pessoal "Esconder Fluxo" (não é
// permissão, é escolha do próprio agente — useHiddenFluxos.ts, header). Usada
// só pelas notificações/badges deste arquivo (tolerância, nova conversa, badge
// "Mês") — a grade/KPIs (DashboardView.vue) já aplicam esse mesmo filtro por
// conta própria em cima de roleFilteredConversations.
export const visibleConversations = computed(() =>
  roleFilteredConversations.value.filter(c => !isFluxoHidden(c.fluxo_id))
)

// Badge no botão "Mês" da Visão Geral — avisa quando há conversa(s) NÃO
// resolvida(s) fora da semana atual (mas dentro do mês) que o agente logado
// tem permissão de ver, mesmo com o período "Esta semana" selecionado.
// monthScopeRaw vem de uma busca à parte em loadData() (só quando o período
// atual é "week"), e passa pelo mesmo escopo de permissão antes de comparar
// com o que já é exibido. Só conta as ainda pendentes (aberta/pendente) —
// uma resolvida de semana anterior não é uma pendência, não deve acender o aviso.
export const monthOnlyCount = computed(() => {
  if (visaoGeralPeriodo.value === 'month') return 0
  const shownIds = new Set(visibleConversations.value.map(c => c.id))
  return applyRoleScope(monthScopeRaw.value)
    .filter(c => c.status !== 'resolved' && !isFluxoHidden(c.fluxo_id) && !shownIds.has(c.id))
    .length
})

// Também usado no push de "Nova conversa" (loadData) — sem isso a notificação
// (e o link pra abrir a conversa a partir dela) ignora o toggle e alerta o
// agente de fallback sobre conversas de fluxo que não são dele. Exportada:
// DashboardView.vue também usa em filteredConversations (filtro "Meus Fluxos").
export function passesFluxoFilter(fluxoId: number | null): boolean {
  if (!headerMyFluxosFilter.value || !authUser.value) return true
  return fluxoId !== null && myFluxoIds.value.includes(fluxoId)
}

/* ── Countdown do botão "Atualizar" ───────────────────────────────────── */
// Sempre passa por aqui pra (re)armar o countdown — nunca criar setInterval direto,
// senão dois timers acabam brigando pelo mesmo countdown.value (contagem "maluca").
function startCountdown(seconds: number) {
  if (countdownId) clearInterval(countdownId)
  countdown.value = seconds
  countdownId = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      if (countdownId) clearInterval(countdownId)
      countdownId = null
      loadData()
    }
  }, 1000)
}

/* ── Período (Esta semana / Mês) ──────────────────────────────────────── */
// O endpoint /conversations/filter do Chatwoot só aceita is_greater_than
// (estrito) para created_at, e compara por DIA — ignora a hora. Confirmado
// direto contra a API real: `since` = a própria data de hoje faz o filtro
// virar "created_at > hoje", que exclui TODAS as conversas de hoje (e o
// mesmo vale pro dia 1 do mês). Isso acontece toda semana (segunda-feira,
// início da semana) e todo mês (dia 1). Corrigido subtraindo 1 dia do limite
// antes de enviar — desloca o corte estrito para o dia anterior, tornando o
// dia de início realmente inclusivo.
function monthStartSinceISO(): string {
  const now = new Date()
  const d = new Date(now.getFullYear(), now.getMonth(), 1)
  d.setDate(d.getDate() - 1)
  return d.toISOString()
}

// Pura (sem depender do ref visaoGeralPeriodo) pra dar pra reusar de fora
// deste motor — MobileView.vue precisa do mesmo filtro "Esta semana"/"Mês"
// pra sua própria lista de conversas, sem duplicar a correção do off-by-one
// acima (fácil de esquecer de novo se reimplementado à parte).
export function periodSinceISO(p: VisaoGeralPeriodo): string {
  if (p === 'month') return monthStartSinceISO()
  const now = new Date()
  const d = new Date(now)
  const diffFromMonday = (d.getDay() + 6) % 7 // getDay(): 0=domingo..6=sábado
  d.setDate(d.getDate() - diffFromMonday - 1)
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

const sinceISO = computed<string>(() => periodSinceISO(visaoGeralPeriodo.value))

export function setVisaoGeralPeriodo(p: VisaoGeralPeriodo) {
  if (visaoGeralPeriodo.value === p) return
  visaoGeralPeriodo.value = p
  loadData()
}

// Botão "Visualizar filtro Mês" da notificação (ver monthOnlyCount acima) —
// reage de qualquer rota agora que o engine roda fora do ciclo de vida do
// DashboardView (antes precisava navegar pro Dashboard primeiro pra ter
// alguém "escutando" o clique).
watch(pendingMonthFilter, v => {
  if (!v) return
  pendingMonthFilter.value = false
  setVisaoGeralPeriodo('month')
})

// Notifica a transição 0 → >0 do badge "Mês" (não a cada ciclo enquanto fica
// >0 — só reabre quando zera e sobe de novo). Botão de ação leva direto pro
// filtro Mês (ver requestViewMonthFilter em App.vue/onSWMessage + sw-notif.js).
let monthAlertShown = false
watch(monthOnlyCount, count => {
  if (count > 0 && !monthAlertShown) {
    monthAlertShown = true
    showPushNotification(
      'Conversas pendentes fora da semana',
      'Existem conversas ainda a serem resolvidas em semanas anteriores deste mês.',
      `${window.location.origin}/`,
      'month-filter-pending',
      undefined,
      {
        requireInteraction: true,
        data: { type: 'month-filter' },
        actions: [{ action: 'view-month', title: 'Visualizar filtro Mês' }],
      },
    )
  } else if (count === 0) {
    monthAlertShown = false
  }
})

/* ── Parse webhook response ───────────────────────────────────────────── */
function parse(raw: any): DashboardData {
  const entry   = Array.isArray(raw) ? raw[0] : raw
  const meta    = entry?.data?.meta    || entry?.meta    || {}
  const payload = entry?.data?.payload || entry?.payload || (Array.isArray(raw) ? raw : [])

  const all_count        = meta.all_count        || payload.length
  const assigned_count   = meta.assigned_count   || 0
  const unassigned_count = meta.unassigned_count || 0
  const mine_count       = meta.mine_count       || 0

  const convs: Conversation[] = payload.map((conv: any) => {
    const m = conv.meta || {}
    return {
      id:            conv.id,
      status:        conv.status || 'open',
      channel:       m.channel || '',
      waiting_since: conv.waiting_since || 0,
      unread_count:  conv.unread_count  || 0,
      created_at:    conv.created_at   || 0,
      resolved_at:   conv.resolved_at  || (conv.status === 'resolved' ? (conv.updated_at || 0) : 0),
      contact: {
        id:        m.sender?.id            || 0,
        name:      m.sender?.name         || '—',
        phone:     m.sender?.phone_number || m.sender?.email || '',
        thumbnail: m.sender?.thumbnail    || '',
      },
      assignee: m.assignee ? {
        id:           m.assignee.id,
        name:         m.assignee.name || m.assignee.available_name || '—',
        email:        m.assignee.email || '',
        role:         m.assignee.role  || 'agent',
        thumbnail:    m.assignee.thumbnail || '',
        availability: m.assignee.availability_status || 'offline',
      } : null,
      team: m.team ? {
        id:   m.team.id,
        name: m.team.name || `Time #${m.team.id}`,
      } : null,
      labels: Array.isArray(conv.labels) ? conv.labels : [],
      fluxo_id: conv.custom_attributes?.fluxo_id ?? 1,
      priority: conv.priority || null,
      cpf:  conv.custom_attributes?.cpf  || null,
      cnpj: conv.custom_attributes?.cnpj || null,
    }
  })

  return { all_count, assigned_count, unassigned_count, mine_count, conversations: convs }
}

/* ── Partial update: detecta quais conversas mudaram ──────────────────── */
function computeChanged(prev: Conversation[], next: Conversation[]): Set<number> {
  const changed = new Set<number>()
  const prevMap = new Map(prev.map(c => [c.id, c]))
  for (const c of next) {
    const p = prevMap.get(c.id)
    if (!p) { changed.add(c.id); continue }
    if (
      p.status        !== c.status        ||
      p.unread_count  !== c.unread_count   ||
      p.assignee?.id  !== c.assignee?.id   ||
      p.team?.id      !== c.team?.id       ||
      p.waiting_since !== c.waiting_since  ||
      p.snoozed_until !== c.snoozed_until  ||
      p.labels.join() !== c.labels.join()
    ) changed.add(c.id)
  }
  return changed
}

/* ── Fase 3 do plano de tempo real (docs/planos/PLANO-REALTIME-ACTIONCABLE.md) ──
 * Aplica o patch traduzido (Fase 2/3) em conversations.value, em paralelo ao
 * ciclo de polling normal — que continua existindo do mesmo jeito, como rede
 * de segurança, sem nenhuma mudança. Só em conversas JÁ conhecidas localmente
 * (findIndex !== -1) — uma conversa nova de verdade continua dependendo do
 * próximo poll pra aparecer.
 *
 * Dois tipos de patch, dois níveis de confiança:
 * - "patch" (message.created/updated) — só assignee/unread_count, e o
 *   assignee vem só como id (resolve o objeto completo cruzando com
 *   allAgents.value).
 * - "snapshot" (conversation.updated/assignee.changed) — conversa inteira,
 *   incluindo o Assignee já completo (a própria mensagem não carrega isso).
 *
 * Reaproveita o MESMO mecanismo de "pisca" (changedIds) e de alerta de
 * atribuição (pushAssignmentAlert + showPushNotification) que o poll já
 * usava — não é lógica de UI nova, só um segundo gatilho mais rápido pros
 * mesmos efeitos. Duplicar com o poll (ou entre os dois tipos de patch, que
 * podem chegar em sequência pra uma mesma ação) não é risco: cada aplicação
 * lê conversations.value fresco, então a segunda vez que o mesmo dado chega
 * (ex.: message.created depois de conversation.updated já ter atualizado o
 * assignee) o "nada mudou de fato" abaixo já barra sozinho.
 *
 * Fase 6: conversationId desconhecido localmente não fica mais 100% preso ao
 * poll — dispara uma busca pontual (fetchAndInsertNewConversation) pra essa
 * UMA conversa. Confirmado ao vivo (Fase 6, teste com contato novo): não
 * existe um evento próprio de "conversa criada" — a primeira mensagem de uma
 * conversa nova dispara o MESMO message.created de sempre, só que com um
 * conversation_id inédito, e o payload desse evento é parcial demais
 * (assignee_id/unread_count/last_activity_at, sem contato/status/canal) pra
 * montar a linha da grade sozinho — dá só o gatilho, não o dado.
 */
function applyRealtimePatch(evt: ChatwootRealtimeEvent) {
  const patch = translateChatwootEvent(evt)
  if (!patch) return

  const idx = conversations.value.findIndex(c => c.id === patch.conversationId)
  if (idx === -1) {
    fetchAndInsertNewConversation(patch.conversationId) // fire-and-forget — não pode travar o listener síncrono de eventos
    return
  }

  const prev = conversations.value[idx]
  const next: Conversation = patch.kind === 'snapshot'
    ? patch.conversation
    : buildPatchedConversation(prev, patch.fields)

  applyConversationChange(idx, prev, next)
}

// Set em vez de boolean solto: várias mensagens da mesma conversa nova podem
// chegar em sequência (confirmado ao vivo: message.created + contact.updated
// + message.created de novo, da resposta automática do bot) antes do fetch
// da primeira terminar — sem isso, cada uma dispararia sua própria busca
// redundante pro mesmo conversationId.
const fetchingNewConvIds = new Set<number>()

async function fetchAndInsertNewConversation(convId: number) {
  if (fetchingNewConvIds.has(convId)) return
  fetchingNewConvIds.add(convId)
  try {
    const conv = await fetchConversationById(convId, authUser.value?.access_token || '')
    if (!conv) return
    if (conversations.value.some(c => c.id === convId)) return // o poll já trouxe ela nesse meio-tempo

    conversations.value = [...conversations.value, conv]
    changedIds.value = new Set([...changedIds.value, conv.id])
    if (changedClearTimer) clearTimeout(changedClearTimer)
    changedClearTimer = setTimeout(() => { changedIds.value = new Set() }, 2500)
    // eslint-disable-next-line no-console -- diagnóstico temporário da Fase 6, mesmo padrão usado nas fases anteriores
    console.debug('[NovaConversaRealtime] linha nova inserida via WebSocket', conv.id)
  } finally {
    fetchingNewConvIds.delete(convId)
  }
}

function buildPatchedConversation(prev: Conversation, fields: ConversationPatchFields): Conversation {
  let assignee = prev.assignee
  if ('assigneeId' in fields) {
    const newId = fields.assigneeId
    if (typeof newId === 'number') {
      const agent = allAgents.value.find(a => a.id === newId)
      assignee = agent
        ? { id: agent.id, name: agent.name, email: agent.email, role: agent.role, thumbnail: agent.thumbnail, availability: agent.availability_status }
        : { id: newId, name: '—', email: '', role: 'agent', thumbnail: '', availability: 'offline' }
    } else {
      assignee = null
    }
  }
  const unread_count = typeof fields.unreadCount === 'number' ? fields.unreadCount : prev.unread_count
  return { ...prev, assignee, unread_count }
}

// Mesmos campos que computeChanged() já compara entre dois polls — reaproveita
// o mesmo critério de "o que conta como mudança visível" pros patches em
// tempo real, pra não divergir em dois entendimentos diferentes do que é
// "mudou de verdade".
function applyConversationChange(idx: number, prev: Conversation, next: Conversation) {
  const meaningfulChange =
    prev.status        !== next.status        ||
    prev.unread_count  !== next.unread_count   ||
    prev.assignee?.id  !== next.assignee?.id   ||
    prev.team?.id      !== next.team?.id       ||
    prev.snoozed_until !== next.snoozed_until  ||
    prev.labels.join() !== next.labels.join()

  // Ex.: message.updated só de status de entrega (sent → delivered), ou um
  // snapshot que chega igual ao que já tínhamos — não vale re-renderizar/
  // piscar a linha à toa.
  if (!meaningfulChange) return

  conversations.value[idx] = next

  changedIds.value = new Set([...changedIds.value, next.id])
  if (changedClearTimer) clearTimeout(changedClearTimer)
  changedClearTimer = setTimeout(() => { changedIds.value = new Set() }, 2500)

  const myId = authUser.value?.id
  if (
    myId && next.assignee?.id === myId && prev.assignee?.id !== myId && next.status !== 'resolved' &&
    !isFluxoHidden(next.fluxo_id) // preferência "Esconder Fluxo" — não alerta sobre o que o próprio agente escondeu
  ) {
    pushAssignmentAlert({ convId: next.id, contactName: next.contact.name })
    showPushNotification(
      'Novo atendimento atribuído',
      `Um novo atendimento foi atribuído a você — ${next.contact.name} (#${next.id})`,
      notifConvUrl(next.id),
      `assigned-${next.id}`,
      next.id,
      { requireInteraction: true, actions: [{ action: 'start', title: 'Iniciar atendimento' }] },
    )
  }
}

onChatwootEvent(applyRealtimePatch)

/* ── Notificação de mensagem nova do cliente (message.created/updated) ──
 * Só pra quem já é o assignee da conversa (mesmo critério das notificações
 * de atribuição acima) — sem esse filtro, todo agente logado seria
 * notificado de toda mensagem de todo cliente. message_type 0 = recebida do
 * contato (1 = enviada pelo agente, 2/3 = activity/template) — só esse caso
 * é "cliente respondeu". Ignora se a conversa já está aberta no drawer
 * (currentOpenConvId) — o agente já está vendo a mensagem chegar ali.
 * Dedupe por message.id porque created + updated do mesmo id podem chegar em
 * sequência (ex.: status passa de "sending" pra "delivered" logo depois). */
const notifiedMessageIds = new Set<number>()

function notifyIncomingMessage(evt: ChatwootRealtimeEvent) {
  const translated = translateMessageContent(evt)
  if (!translated) return
  const { message, conversationId, assigneeId } = translated
  if (message.message_type !== 0 || message.private) return

  const myId = authUser.value?.id
  if (!myId) return

  // Nem todo evento de mensagem traz o sub-objeto "conversation" com
  // assignee_id (só quando o Chatwoot decide incluir, ver comentário em
  // chatwootRealtimeTranslator.ts) — cai pro que já está carregado
  // localmente (polling ou snapshot de conversation.updated/assignee.changed)
  // quando o evento em si não trouxe.
  const localConv = conversations.value.find(c => c.id === conversationId)
  const effectiveAssigneeId = assigneeId ?? localConv?.assignee?.id ?? null
  if (effectiveAssigneeId !== myId) {
    console.debug('[MessageRealtime] notificação ignorada — não é o assignee', { conversationId, effectiveAssigneeId, myId })
    return
  }
  if (currentOpenConvId.value === conversationId) {
    console.debug('[MessageRealtime] notificação ignorada — conversa já aberta', conversationId)
    return
  }

  if (notifiedMessageIds.has(message.id)) return
  notifiedMessageIds.add(message.id)
  if (notifiedMessageIds.size > 200) {
    const oldest = notifiedMessageIds.values().next().value
    if (oldest !== undefined) notifiedMessageIds.delete(oldest)
  }

  const senderName = message.sender?.available_name || message.sender?.name || localConv?.contact.name || 'Cliente'
  const body = message.content?.trim() || (message.attachments?.length ? '📎 Anexo' : 'Nova mensagem')

  console.debug('[MessageRealtime] disparando notificação de mensagem', conversationId, message.id)
  showPushNotification(
    senderName,
    body,
    notifConvUrl(conversationId),
    `msg-${message.id}`,
    conversationId,
    {
      icon: message.sender?.thumbnail,
      timestamp: message.created_at ? message.created_at * 1000 : undefined,
      actions: [{ action: 'open', title: 'Ver conversa' }],
    },
  )
}

onChatwootEvent(notifyIncomingMessage)

/* ── Retry com backoff exponencial + circuit breaker ──────────────────── */
// Em operação normal (circuito fechado) tenta até 3x — cobre falhas
// transitórias de rede. Em modo de teste (half-open, após o N8N ter ficado
// indisponível por FAILURE_THRESHOLD ciclos seguidos) faz só 1 tentativa,
// pra não gastar ~14s de retries internos sondando um serviço que pode
// ainda estar fora — quem já governa quando sondar de novo é o circuito.
async function fetchWithRetry(url: string, init: Parameters<typeof fetch>[1]): Promise<Response> {
  const delays      = [2000, 4000, 8000]
  const maxAttempts = circuitState.value === 'half-open' ? 1 : 3
  let lastErr: Error = new Error('Falha na requisição')
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const res = await fetch(url, init)
      if (res.ok) return res
      throw new Error(`HTTP ${res.status}`)
    } catch (err: any) {
      if (err?.name === 'AbortError') throw err
      lastErr = err
      if (i < maxAttempts - 1) {
        toast(`Sem resposta — tentativa ${i + 2}/${maxAttempts}…`, true)
        await new Promise<void>(r => setTimeout(r, delays[i]))
      }
    }
  }
  throw lastErr
}

/* ── Load data ─────────────────────────────────────────────────────────── */
export async function loadData() {
  // Circuito aberto: N8N já falhou 3 ciclos seguidos. Pula a chamada de rede
  // (e os retries internos) até o cooldown expirar — evita martelar um
  // serviço que sabemos estar fora, e abrevia o ciclo pro próximo countdown.
  if (!canAttempt()) {
    dataStale.value = true
    failCount++
    toast(`N8N indisponível — nova tentativa em ${secondsUntilRetry()}s`, true)
    startCountdown(secondsUntilRetry())
    return
  }

  currentFetchController?.abort()
  currentFetchController = new AbortController()
  const { signal } = currentFetchController

  loading.value = true
  if (countdownId) clearInterval(countdownId)

  try {
    // Ciclo "leve": todo ciclo que NÃO é o primeiro após login, não teve o
    // período trocado (Tudo/Hoje/Semana/Mês) e não vem logo depois de uma
    // falha (failCount > 0 — inclui o "Tentar agora" da stale-bar) busca só
    // as conversas que ainda podem mudar de status sozinhas (open/pending/
    // snoozed). "Resolvida" não volta a mudar por conta própria, então essas
    // ficam só em memória (mescladas mais abaixo) — bem mais leve que buscar
    // tudo de novo a cada 180s. Nos outros casos, busca completa mesmo —
    // reconciliar de menos justo num retry ou numa troca de período é pior
    // que o custo de uma busca cheia a mais.
    const needsFullFetch = isFirstLoad || failCount > 0 || sinceISO.value !== lastSinceISO
    lastSinceISO = sinceISO.value

    // Escopo "Meus Fluxos" (perfil EC-ISBET, toggle no header) — só restringe
    // o ciclo LEVE, nunca a busca completa: o cache local (conversations.value)
    // precisa sempre ter TODOS os fluxos depois de uma busca completa, senão
    // ligar/desligar o toggle no meio da sessão ficaria preso ao que o último
    // ciclo leve trouxe em vez de recalcular na hora sobre o que já tem em
    // memória (é só um filtro client-side, ver passesFluxoFilter).
    const fluxoScopeActive =
      !needsFullFetch && headerMyFluxosFilter.value && myFluxoIdsReady && myFluxoIds.value.length > 0

    const db = useAdminDB()
    const [res, agentRes, agentStatusRows] = await Promise.all([
      fetchWithRetry(WEBHOOK, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          command:      'get_conversations',
          timestamp:    Date.now(),
          access_token: authUser.value?.access_token,
          // Filtro de período (Visão Geral: Tudo/Hoje/Esta semana/Mês) — quando
          // preenchido, o n8n busca só conversas com created_at >= since direto
          // no Chatwoot, reduzindo o volume trazido (não é só filtro de UI).
          since: sinceISO.value,
          // Ciclo leve (ver needsFullFetch acima) — requer "Listar Chats" v7
          // no n8n (suporta body.statuses); sem ele o n8n ignora o campo e
          // devolve tudo, o merge abaixo continua correto, só sem o ganho de
          // velocidade.
          ...(needsFullFetch ? {} : { statuses: ['open', 'pending', 'snoozed'] }),
          // Escopo "Meus Fluxos" (ver fluxoScopeActive acima) — requer
          // "Listar Chats" v8 (suporta body.fluxo_ids); mesma degradação
          // graciosa do statuses acima sem a versão nova do node.
          ...(fluxoScopeActive ? { fluxo_ids: myFluxoIds.value } : {}),
          // Conversa sem agente E sem time (ainda só com o robô/Typebot/IA,
          // antes de ser roteada) — segue o mesmo toggle "Geral" do seletor
          // "Esconder Fluxo" (ver watch(isFluxoHidden(1)) acima). Requer
          // "Listar Chats" v9 (suporta body.exclude_unassigned_no_team);
          // mesma degradação graciosa sem a versão nova do node.
          exclude_unassigned_no_team: isFluxoHidden(1),
        }),
        signal,
      }),
      fetch(`/chatwoot-api/api/v1/accounts/${CHATWOOT_ACCOUNT_ID}/agents`, {
        headers: { api_access_token: authUser.value?.access_token ?? '' },
        signal,
      }).catch(() => null),
      db.loadAgentStatus().catch(() => []),
    ])
    const raw  = await res.json()
    const data = parse(raw)
    data.conversations = filterOutBlacklisted(data.conversations)

    // Sobrepõe availability com dados em tempo real da API de agentes
    if (agentRes?.ok) {
      const agentList = await agentRes.json().catch(() => [])
      if (Array.isArray(agentList)) {
        // Prefere o status guardado por nós (o que o próprio agente escolheu no
        // dropdown) sobre availability_status do Chatwoot, que é presença
        // computada via ActionCable/auto_offline e fica travada em offline pra
        // quem só acessa via esta dashboard (ver migração v14).
        const statusByUser = new Map(agentStatusRows.map(r => [r.user_id, r.availability]))
        const availMap: Record<number, string> = {}
        agentList.forEach((a: any) => {
          availMap[a.id] = statusByUser.get(Number(a.id)) ?? a.availability_status ?? 'offline'
        })
        // Salva a lista completa de agentes para a aba Agentes
        allAgents.value = agentList.map((a: any) => ({
          id:                  Number(a.id),
          name:                String(a.name ?? ''),
          email:               String(a.email ?? ''),
          thumbnail:           String(a.thumbnail ?? ''),
          role:                String(a.role ?? 'agent'),
          availability_status: availMap[Number(a.id)],
        }))
        // Não sincroniza o status do usuário atual a partir daqui: `availability_status`
        // é a presença computada pelo Chatwoot (auto_offline/ActionCable), que fica
        // "offline" pra quem só acessa via esta dashboard — sobrescrever o status que o
        // próprio agente setou no dropdown a cada polling fazia ele "voltar" pra offline
        // sozinho. O status próprio já é mantido por useAvailability.ts (ação explícita
        // do usuário + sync entre abas via localStorage).
        data.conversations = data.conversations.map(c => ({
          ...c,
          assignee: c.assignee ? {
            ...c.assignee,
            availability: availMap[c.assignee.id] || c.assignee.availability,
          } : null,
        }))
      }
    }

    const prevMap    = new Map(conversations.value.map(c => [c.id, c]))
    const newChanged = computeChanged(conversations.value, data.conversations)

    if (needsFullFetch) {
      conversations.value = data.conversations
    } else {
      // Mescla o ciclo leve: atualiza quem veio agora (open/pending/snoozed,
      // dentro do escopo de fluxo se fluxoScopeActive), mantém o resto como já
      // estava, e rebaixa pra "resolved" localmente quem: (a) era open/pending/
      // snoozed antes, e (b) estaria dentro do que ESTE ciclo pediu (mesmo
      // status e, se fluxoScopeActive, mesmo fluxo) — só assim a ausência dela
      // no resultado só pode significar que resolveu nesse meio-tempo, em vez
      // de simplesmente estar fora do fluxo pedido desta vez. Sem esse cuidado,
      // uma conversa de outro fluxo (fora do escopo "Meus Fluxos" deste ciclo)
      // seria marcada como resolvida por engano.
      const freshMap = new Map(data.conversations.map(c => [c.id, c]))
      const fluxoIdsSet = fluxoScopeActive ? new Set(myFluxoIds.value) : null
      const merged = conversations.value.map(c => {
        const fresh = freshMap.get(c.id)
        if (fresh) { freshMap.delete(c.id); return fresh }
        const wasInFetchScope =
          (c.status === 'open' || c.status === 'pending' || c.status === 'snoozed') &&
          (!fluxoIdsSet || (c.fluxo_id !== null && fluxoIdsSet.has(c.fluxo_id)))
        if (wasInFetchScope) return { ...c, status: 'resolved' }
        return c
      })
      conversations.value = [...merged, ...freshMap.values()]
    }

    // Badge "Mês" (monthOnlyCount): só busca à parte quando o período atual é
    // "week" — com "month" a busca acima já cobre tudo, não precisa duplicar.
    // Fire-and-forget (Fase 2 do plano de carregamento pós-login): antes essa
    // segunda chamada era `await`ada aqui dentro, segurando loadData() inteiro
    // — e por extensão scopeReady — por um round-trip extra que só alimenta um
    // badge secundário do seletor de período. monthOnlyCount é computed sobre
    // monthScopeRaw, então o badge atualiza sozinho via reatividade assim que
    // a resposta chegar, sem travar o resto da tela.
    if (visaoGeralPeriodo.value === 'week') {
      fetchWithRetry(WEBHOOK, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          command:      'get_conversations',
          timestamp:    Date.now(),
          access_token: authUser.value?.access_token,
          since:        monthStartSinceISO(),
          exclude_unassigned_no_team: isFluxoHidden(1),
        }),
        signal,
      })
        .then(monthRes => monthRes.json())
        .then(raw => { monthScopeRaw.value = filterOutBlacklisted(parse(raw).conversations) })
        .catch(() => {
          // Falhou a busca extra do badge — mantém a contagem anterior (não é crítico).
        })
    } else {
      monthScopeRaw.value = data.conversations
    }

    if (newChanged.size > 0) {
      if (changedClearTimer) clearTimeout(changedClearTimer)
      changedIds.value = newChanged
      changedClearTimer = setTimeout(() => { changedIds.value = new Set() }, 2500)
    }

    // Notificar conversas realmente novas (não na carga inicial) — só as com
    // status "aberto"; outros status (pendente, resolvida etc.) não notificam.
    if (!isFirstLoad) {
      for (const conv of visibleConversations.value) {
        if (!passesFluxoFilter(conv.fluxo_id)) continue
        if (!prevMap.has(conv.id) && conv.status === 'open') {
          showPushNotification(
            `Nova conversa — ${conv.contact.name}`,
            `#${conv.id}${conv.team ? ` · ${conv.team.name}` : ''}`,
            notifConvUrl(conv.id),
            `new-conv-${conv.id}`,
            conv.id,
          )
          toast(`Nova conversa #${conv.id} — ${conv.contact.name}`)
        }
      }

      // Alerta de atribuição — dispara só na transição pra "atribuída a mim"
      // (conversa nova já assignada, ou reassignada), nunca a cada ciclo de
      // polling para quem já era o assignee. Modal bloqueante (AssignmentAlertModal,
      // montado em App.vue) + notificação OS (requireInteraction fica visível até
      // o agente interagir, mesmo com a aba minimizada).
      const myId = authUser.value?.id
      if (myId) {
        for (const conv of data.conversations) {
          if (conv.assignee?.id !== myId) continue
          if (conv.status === 'resolved') continue
          if (prevMap.get(conv.id)?.assignee?.id === myId) continue
          if (isFluxoHidden(conv.fluxo_id)) continue // preferência "Esconder Fluxo"
          pushAssignmentAlert({ convId: conv.id, contactName: conv.contact.name })
          showPushNotification(
            'Novo atendimento atribuído',
            `Um novo atendimento foi atribuído a você — ${conv.contact.name} (#${conv.id})`,
            notifConvUrl(conv.id),
            `assigned-${conv.id}`,
            conv.id,
            { requireInteraction: true, actions: [{ action: 'start', title: 'Iniciar atendimento' }] },
          )
        }
      }
    }

    // Catch-up do popup "Novo atendimento atribuído" via banco (call_conv,
    // migração v28) — cobre o que a detecção ao vivo acima (bloco
    // "Alerta de atribuição") não pode ver: se a atribuição aconteceu
    // enquanto este agente estava offline/com a aba fechada, não existe
    // transição pra detectar num diff de polling. Roda em TODO ciclo,
    // inclusive o primeiro (isFirstLoad) — ao contrário do bloco acima, aqui
    // é exatamente o cold-start que precisa ser coberto. Só retorna linhas
    // ainda não dispensadas (dismissed = 0); pushAssignmentAlert já deduplica
    // por convId contra o que a detecção ao vivo já tiver empilhado.
    //
    // NÃO USA AWAIT AQUI — é fire-and-forget de propósito. Isso já bloqueou
    // o ciclo inteiro de polling (dashboard "congelado", nada atualizava) uma
    // vez em que o webhook isolado de call_conv ficou lento/instável: como
    // esse await ficava ANTES do startCountdown() no finally, o próximo ciclo
    // nunca era re-armado até essa chamada (não crítica) terminar. Sem await,
    // uma falha ou lentidão aqui nunca mais atrasa conversations.value,
    // isFirstLoad, failCount/circuit breaker ou o countdown do botão
    // "Atualizar".
    {
      const myIdForCallConv = authUser.value?.id
      if (myIdForCallConv) {
        getPendingCallConv(myIdForCallConv)
          .then(pending => {
            for (const row of pending) {
              // PendingCallConvRow não carrega fluxo_id — cruza com o que já
              // foi carregado localmente pra aplicar a preferência "Esconder
              // Fluxo". Sem match local (raro), segue mostrando: não dá pra
              // confirmar que devia esconder, e é melhor mostrar de mais do
              // que perder um alerta de atribuição de verdade.
              const localConv = conversations.value.find(c => c.id === row.conversationId)
              if (localConv && isFluxoHidden(localConv.fluxo_id)) continue
              pushAssignmentAlert({ convId: row.conversationId, contactName: row.contactName })
            }
          })
          .catch(() => { /* catch-up não pode quebrar o ciclo de polling principal */ })
      }
    }

    // Não-lidas do chat entre agentes (list_agent_unread_counts) — de propósito
    // só roda aqui (login/clique em "Atualizar"/fim do countdown), nunca em
    // timer próprio: useAgentChat.poll() só cobre a thread aberta agora. Mesmo
    // motivo/padrão de fire-and-forget do catch-up de call_conv acima.
    {
      const myIdForAgentChat = authUser.value?.id
      if (myIdForAgentChat && useAdmin().hasPermission(myIdForAgentChat, userTeamIds.value, 'agentChat.view')) {
        useAgentChat().loadUnread(myIdForAgentChat)
      }
    }

    isFirstLoad = false

    failCount        = 0
    recordSuccess()
    dataStale.value  = false
    lastUpdate.value = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })

    checkToleranceNotifications()
    toast(
      needsFullFetch
        ? `✓ ${data.conversations.length} conversa(s) carregada(s)`
        : `✓ Atualizado — ${data.conversations.length} em aberto/pendente/adiada`
    )

  } catch (err: any) {
    if (err?.name === 'AbortError') return
    failCount++
    recordFailure()
    console.error('[Dashboard]', err)
    dataStale.value = true
    toast(`Sem conexão — dados de ${lastUpdate.value || '—'}`, true)
  } finally {
    loading.value = false

    // Jitter (0–30s aleatório) distribui os ciclos dos 30 agentes ao longo do tempo,
    // evitando que todos disparem para o N8N no mesmo segundo (thundering herd).
    // Backoff dobra o intervalo base a cada falha consecutiva (máx. 10 min).
    const jitter  = Math.floor(Math.random() * 30)
    const backoff = failCount > 0
      ? Math.min(AUTO_REFRESH * Math.pow(2, failCount - 1), 600)
      : AUTO_REFRESH
    const next = backoff + jitter

    startCountdown(next)
  }
}

// Times do próprio agente logado (myTeamIds/setUserTeams) — usado por
// resolveRoleScope() pra escopar o que ele pode ver, em praticamente toda
// view. Até aqui isso vivia em LoginView.vue::handleLogin(), bloqueando a
// navegação pro Dashboard até essa varredura (cara — 1 request por time)
// terminar. Movido pra rodar em paralelo com loadData() dentro de
// startDashboardEngine() — o mesmo overlay de carregamento que
// DashboardView.vue já mostra enquanto espera loadData() passa a cobrir os
// dois juntos, sem risco da grade renderizar sem escopo (o overlay só
// esconde depois que essa promise inteira resolve).
export async function loadMyTeamIds(): Promise<void> {
  const user = authUser.value
  if (!user) return

  // GET /teams NÃO é escopado por membership pelo Chatwoot pra usuário comum
  // (confirmado em produção: supervisor com 1 time no team_members via essa
  // API voltava com todos os times da conta) — só filtrando membro a membro
  // é que dá pra saber quais times são "meus" de verdade, tanto pra
  // `administrator` quanto pra qualquer outro role nativo do Chatwoot.
  const { load: loadTeamsWithMembers } = useTeamsWithMembers()
  const allTeams = await loadTeamsWithMembers(CHATWOOT_ACCOUNT_ID, user.access_token)
  const myTeamIds = allTeams.filter(t => t.memberIds.includes(user.id)).map(t => t.id)
  await store.dispatch('setUserTeams', myTeamIds)
}

/* ── Bootstrap — chamar uma única vez a partir de App.vue ─────────────── */
let _startPromise: Promise<void> | null = null

export function startDashboardEngine(): Promise<void> {
  if (_startPromise) return _startPromise
  _startPromise = (async () => {
    // Clique em "Visualizar filtro Mês" antes do primeiro carregamento (app
    // cold-start via notificação) — aplica no período já na primeira busca,
    // evitando uma segunda logo em seguida.
    if (pendingMonthFilter.value) {
      pendingMonthFilter.value = false
      visaoGeralPeriodo.value = 'month'
    }

    // Fase 4 (reaberta) do plano de otimização de carregamento — opt-in via
    // localStorage.setItem('debug_n8n_timing','1') + reload, pra medir ao
    // vivo se a rajada de chamadas ao webhook compartilhado (achado 1) roda
    // em paralelo ou fica em fila. Logado no fim de loadData() em si — não
    // depois do scopeReady lá embaixo — porque loadMyTeamIds() (achado 6,
    // N+1 de team_members) pode levar bem mais tempo que loadData() sozinho;
    // esperar scopeReady empurraria a rajada real pra fora da janela padrão
    // de 15s, capturando só ruído de polling de fundo (notify, unread etc.)
    // em vez das chamadas do próprio boot. Silencioso por padrão.
    const debugN8nTiming = typeof window !== 'undefined' && window.localStorage?.getItem('debug_n8n_timing') === '1'

    // Em paralelo — loadMyTeamIds() já era a única coisa bloqueante que
    // sobrava fora daqui (ver comentário na função acima). Pulada por
    // completo com "Abrir em Modo Foco" ativo (useFocusModePreference.ts):
    // o Modo Foco escopa por assignee (useMyConversations.ts), não por time,
    // então a varredura de team_members (1 request por time) não tem pra que
    // rodar — é exatamente o ganho de carregamento prometido no texto do
    // toggle em ProfileView.vue.
    const tasks: Promise<unknown>[] = [
      loadData().then(() => {
        // setTimeout, não chamada direta: a entrada de Resource Timing da
        // própria requisição que acabou de resolver às vezes ainda não foi
        // gravada no buffer do navegador no instante exato em que a promise
        // de loadData() resolve — ler na hora podia voltar vazio mesmo
        // segundos depois da chamada real ter acontecido.
        if (debugN8nTiming) setTimeout(() => logN8nBurstTiming(), 300)
      }),
    ]
    if (!openInFocusMode.value) tasks.push(loadMyTeamIds())
    await Promise.all(tasks)

    // Fase 2 do plano de carregamento progressivo — a partir daqui,
    // conversations + userTeamIds já são confiáveis pra renderizar qualquer
    // coisa role-scoped. O resto desta função (allTeamsWithMembers,
    // myFluxoIds) já era fire-and-forget antes disso existir — não muda.
    scopeReady.value = true

    // Membership de times para o filtro por permissão da aba Agentes (não bloqueia
    // o carregamento principal) — usa o cache de sessão de useTeamsWithMembers
    // (mesma chave accountId:token de loadMyTeamIds() acima — reusa o cache
    // em vez de buscar de novo). Também pulada com Modo Foco ativo — se o
    // agente abrir Admin/Agentes/Times manualmente mesmo assim, essas telas
    // já chamam loadTeamsWithMembers() no próprio onMounted e buscam sob
    // demanda (cache de useTeamsWithMembers.ts é por accountId:token, não se
    // importa se veio daqui ou de lá).
    if (!openInFocusMode.value) {
      const { load: loadTeamsWithMembers } = useTeamsWithMembers()
      loadTeamsWithMembers(CHATWOOT_ACCOUNT_ID, authUser.value?.access_token ?? '')
        .then(teams => { allTeamsWithMembers.value = teams })
        .catch(() => undefined)
    }

    // Fluxos que este agente atende (perfil "EC - ISBET") — usado pelo toggle "Meus
    // Fluxos" no header (ver DashHeader.vue → isEcIsbet). Categoria pode ter sido
    // gravada tanto em agent_categories (aba Agentes) quanto em team_fallback_agents
    // (Times → Fallback) — junta as duas e ignora o team_id: esse perfil vê os
    // fluxos de forma global, independente de time.
    if (authUser.value) {
      const uid = authUser.value.id
      const db  = useAdminDB()
      Promise.all([
        db.loadAgentCategories().catch(() => []),
        db.loadTeamFallbackAgents().catch(() => []),
      ]).then(([agentCats, fallbackCats]) => {
        const ids = new Set<number>()
        ;[...agentCats, ...fallbackCats]
          .filter(r => r.user_id === uid)
          .forEach(r => withDefaultFluxos(r.categories, r.fluxo_ids).forEach(id => ids.add(id)))
        myFluxoIds.value = [...ids]
        myFluxoIdsReady = true
      })
    }
  })()
  return _startPromise
}

/**
 * Fase 4.2 do plano de otimização de login (docs/planos/PLANO-OTIMIZACAO-LOGIN.md):
 * chamado no logout. `_startPromise` é o mais crítico dos três — sem zerar
 * ele, `startDashboardEngine()` continuaria idempotente pra sempre e um
 * agente B logando na mesma aba NUNCA rodaria `loadData()` de novo, ficando
 * preso vendo a grade (e os contadores/toasts) do agente A indefinidamente.
 * Não mexe em `toleranceMinutes`/`visaoGeralPeriodo` (preferências de UI
 * persistidas em localStorage, não dado de sessão de um agente específico).
 */
export function stopDashboardEngine(): void {
  _startPromise = null
  currentFetchController?.abort()
  currentFetchController = null
  if (countdownId)       { clearInterval(countdownId);      countdownId = null }
  if (changedClearTimer) { clearTimeout(changedClearTimer); changedClearTimer = null }
  if (toastTimer)        { clearTimeout(toastTimer);        toastTimer = null }
  failCount    = 0
  isFirstLoad  = true
  lastSinceISO = undefined
  myFluxoIdsReady = false
  notifiedCycles.clear()
  fetchingNewConvIds.clear()

  useConversationStore().setConversations([])
  allAgents.value          = []
  allTeamsWithMembers.value = []
  myFluxoIds.value         = []
  monthScopeRaw.value      = []
  dataStale.value          = false
  changedIds.value         = new Set()
  activeNotifications.value = []
  toastVisible.value = false
  toastMessage.value = ''
  toastError.value   = false
  scopeReady.value   = false // Fase 2 do plano de carregamento progressivo — mesmo motivo do _startPromise acima
}
